import MiniSearch from 'minisearch';
import { supabase } from './supabase.js';
import { coerceUrl } from './url.js';

// Fields we read from the bibliography table. Keep this list in sync with
// what we render in the UI and export.
const SELECT_COLUMNS = [
  'id',
  'type',
  'title',
  'authors',
  'year',
  'language',
  'language_display',
  'keywords',
  'abstract',
  'doi',
  'url',
  'publisher',
  'place',
  'edition',
  'journal',
  'volume',
  'issue',
  'pages',
  'editors',
  'booktitle',
  'conference',
  'announce_in_whats_new',
].join(', ');

// Supabase caps non-paginated SELECT at 1000 rows, so for a multi-thousand
// row table we have to ask for several pages. Earlier we did this with a
// `for (;;) await` loop — three sequential round-trips at ~500 ms each.
// On slow connections (or when the navbar's pending-count poller and any
// in-flight queries from a previous SPA route were also competing for
// the connection) the total stretched past our 20 s page-load timeout
// and the user saw "Something went wrong".
//
// Now we fetch a fast count first, then fire every page in parallel.
// Three pages × 500 ms become ~500 ms total instead of ~1.5 s — and the
// page no longer hits the timeout when the connection is busy.
export async function fetchAllPublished() {
  const PAGE = 1000;

  const { count, error: countError } = await supabase
    .from('bibliography')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'published');
  if (countError) throw countError;

  const total = count ?? 0;
  if (total === 0) return [];

  const numPages = Math.ceil(total / PAGE);
  const ranges = Array.from({ length: numPages }, (_, i) => [i * PAGE, (i + 1) * PAGE - 1]);

  const results = await Promise.all(
    ranges.map(([from, to]) =>
      supabase
        .from('bibliography')
        .select(SELECT_COLUMNS)
        .eq('status', 'published')
        .order('year', { ascending: false, nullsFirst: false })
        .order('title', { ascending: true })
        .range(from, to)
        .then(({ data, error }) => {
          if (error) throw error;
          return data ?? [];
        }),
    ),
  );
  return results.flat();
}

export function buildSearchIndex(entries) {
  const index = new MiniSearch({
    fields: ['title', 'authorsText', 'abstract', 'keywordsText', 'journal', 'booktitle', 'editorsText'],
    storeFields: ['id'],
    searchOptions: {
      boost: { title: 2, authorsText: 1.5 },
      fuzzy: 0.2,
      prefix: true,
    },
  });
  index.addAll(
    entries.map((e) => ({
      id: e.id,
      title: e.title ?? '',
      authorsText: (e.authors ?? []).join(' '),
      abstract: e.abstract ?? '',
      keywordsText: (e.keywords ?? []).join(' '),
      journal: e.journal ?? '',
      booktitle: e.booktitle ?? '',
      editorsText: (e.editors ?? []).join(' '),
    })),
  );
  return index;
}

// Return entries matching the search query (most-relevant first). Empty
// query returns all entries unchanged.
export function searchEntries(entries, index, query) {
  const q = query.trim();
  if (!q) return entries;
  const hits = index.search(q);
  const byId = new Map(entries.map((e) => [e.id, e]));
  return hits.map((h) => byId.get(h.id)).filter(Boolean);
}

// Lowercase, strip accents and punctuation, and split into words, so that
// "Hans-Georg Gadamer", "Gadamer, Hans-Georg" and "gadamer hans georg" all
// reduce to the same set of tokens.
function nameTokens(s) {
  return String(s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .replace(/ß/g, 'ss')
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

// True when every word the user typed appears (as a prefix of a word) in the
// author's name, whatever the order. Prefix matching keeps "Gad" → Gadamer
// working while the user is still typing.
export function authorMatches(author, query) {
  const wanted = nameTokens(query);
  if (!wanted.length) return true;
  const have = nameTokens(author);
  return wanted.every((w) => have.some((h) => h.startsWith(w)));
}

// Apply the structured filters. Each filter is optional; missing means "all".
export function filterEntries(entries, filters) {
  return entries.filter((e) => {
    if (filters.types?.length && !filters.types.includes(e.type)) return false;
    if (filters.language && e.language !== filters.language) return false;
    if (filters.author?.trim()) {
      if (!(e.authors ?? []).some((a) => authorMatches(a, filters.author))) return false;
    }
    if (filters.keywords?.length) {
      const have = new Set((e.keywords ?? []).map((k) => k.toLowerCase()));
      const wanted = filters.keywords.map((k) => k.toLowerCase());
      if (!wanted.every((k) => have.has(k))) return false;
    }
    if (filters.yearMin != null && (e.year == null || e.year < filters.yearMin)) return false;
    if (filters.yearMax != null && (e.year == null || e.year > filters.yearMax)) return false;
    return true;
  });
}

// Sort directions:
//   year_desc, year_asc, author_asc, title_asc
export function sortEntries(entries, mode) {
  const sorted = [...entries];
  switch (mode) {
    case 'year_asc':
      sorted.sort((a, b) => (a.year ?? Infinity) - (b.year ?? Infinity) || a.title.localeCompare(b.title));
      break;
    case 'year_desc':
      sorted.sort((a, b) => (b.year ?? -Infinity) - (a.year ?? -Infinity) || a.title.localeCompare(b.title));
      break;
    case 'author_asc':
      sorted.sort((a, b) => {
        const aa = (a.authors ?? [])[0] ?? '';
        const bb = (b.authors ?? [])[0] ?? '';
        return aa.localeCompare(bb) || (a.year ?? 0) - (b.year ?? 0);
      });
      break;
    case 'title_asc':
      sorted.sort((a, b) => a.title.localeCompare(b.title));
      break;
    default:
      // already ordered by fetch
      break;
  }
  return sorted;
}

// Build the lists that drive the filter widgets.
export function deriveFacets(entries) {
  const types = new Set();
  const languages = new Map();
  const keywords = new Map();
  let yearMin = Infinity;
  let yearMax = -Infinity;
  for (const e of entries) {
    if (e.type) types.add(e.type);
    if (e.language) {
      languages.set(e.language, e.language_display ?? e.language);
    }
    for (const k of e.keywords ?? []) {
      keywords.set(k, (keywords.get(k) ?? 0) + 1);
    }
    if (typeof e.year === 'number') {
      if (e.year < yearMin) yearMin = e.year;
      if (e.year > yearMax) yearMax = e.year;
    }
  }
  return {
    types: [...types].sort(),
    languages: [...languages.entries()]
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    keywords: [...keywords.entries()]
      .map(([keyword, count]) => ({ keyword, count }))
      .sort((a, b) => b.count - a.count),
    yearMin: Number.isFinite(yearMin) ? yearMin : null,
    yearMax: Number.isFinite(yearMax) ? yearMax : null,
  };
}

export const EMPTY_FILTERS = {
  types: [],
  language: '',
  author: '',
  keywords: [],
  yearMin: null,
  yearMax: null,
};

export function hasActiveFilters(filters) {
  return (
    filters.types.length > 0 ||
    !!filters.language ||
    !!filters.author.trim() ||
    filters.keywords.length > 0 ||
    filters.yearMin != null ||
    filters.yearMax != null
  );
}

export function countActiveFilters(filters) {
  let n = 0;
  if (filters.types.length) n += 1;
  if (filters.language) n += 1;
  if (filters.author.trim()) n += 1;
  if (filters.keywords.length) n += 1;
  if (filters.yearMin != null || filters.yearMax != null) n += 1;
  return n;
}

// ────────── Form data shape & defaults ──────────

export const EMPTY_ENTRY = {
  type: 'book',
  title: '',
  authors: [],
  year: null,
  language: '',
  language_display: '',
  keywords: [],
  abstract: '',
  doi: '',
  url: '',
  publisher: '',
  place: '',
  edition: '',
  journal: '',
  volume: '',
  issue: '',
  pages: '',
  editors: [],
  booktitle: '',
  conference: '',
};

// Coerce a form-state object into the row payload Supabase expects.
// Strips empty strings → NULL; converts year to integer or null.
function normalizeForInsert(form) {
  const blank = (v) => (v == null || v === '' ? null : v);
  return {
    type: form.type,
    title: form.title?.trim(),
    authors: (form.authors ?? []).map((a) => a.trim()).filter(Boolean),
    year: form.year === '' || form.year == null ? null : parseInt(form.year, 10),
    language: blank(form.language?.trim?.() ?? form.language),
    language_display: blank(form.language_display?.trim?.() ?? form.language_display),
    keywords: (form.keywords ?? []).map((k) => k.trim()).filter(Boolean),
    abstract: blank(form.abstract),
    doi: blank(form.doi?.trim?.() ?? form.doi),
    url: coerceUrl(form.url),
    publisher: blank(form.publisher),
    place: blank(form.place),
    edition: blank(form.edition),
    journal: blank(form.journal),
    volume: blank(form.volume),
    issue: blank(form.issue),
    pages: blank(form.pages),
    editors: (form.editors ?? []).map((e) => e.trim()).filter(Boolean),
    booktitle: blank(form.booktitle),
    conference: blank(form.conference),
  };
}

// Validate before submit — returns null if OK, otherwise a translatable error key.
export function validateEntry(form) {
  if (!form.type) return 'bibliography.form.errors.typeRequired';
  if (!form.title?.trim()) return 'bibliography.form.errors.titleRequired';
  if (!(form.authors ?? []).some((a) => a.trim())) {
    return 'bibliography.form.errors.authorsRequired';
  }
  if (form.year !== null && form.year !== '' && form.year !== undefined) {
    const n = parseInt(form.year, 10);
    if (Number.isNaN(n) || n < 0 || n > 3000) return 'bibliography.form.errors.yearInvalid';
  }
  return null;
}

// Compute the diff between an original published entry and a form patch,
// returning { suggested_changes: { field: newValue } } with only changed fields.
export function diffEntries(original, edited) {
  const next = normalizeForInsert(edited);
  const changes = {};
  for (const key of Object.keys(next)) {
    const a = original[key];
    const b = next[key];
    if (Array.isArray(a) || Array.isArray(b)) {
      const aa = Array.isArray(a) ? a : [];
      const bb = Array.isArray(b) ? b : [];
      if (aa.length !== bb.length || aa.some((v, i) => v !== bb[i])) {
        changes[key] = bb;
      }
    } else if ((a ?? null) !== (b ?? null)) {
      changes[key] = b;
    }
  }
  return changes;
}

// ────────── Mutations ──────────

// Create a new entry. status = 'pending' for participants, 'published' for admins.
// announce_in_whats_new is only written here (not on update): once the row is
// published, a DB trigger (migration 019) turns the flag into a What's New
// notice that expires after 30 days.
export async function createEntry(form, { authorId, asAdmin }) {
  const row = {
    ...normalizeForInsert(form),
    announce_in_whats_new: Boolean(form.announce_in_whats_new),
    status: asAdmin ? 'published' : 'pending',
    submitted_by: authorId,
  };
  const { data, error } = await supabase
    .from('bibliography')
    .insert(row)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// Batch-create entries — used by the book-chapter importer. Each row follows
// the same status routing as createEntry (pending for participants, published
// for admins). A single insert keeps it one round-trip; RLS still checks every
// row. Returns the inserted rows.
export async function createEntries(forms, { authorId, asAdmin }) {
  const rows = forms.map((form) => ({
    ...normalizeForInsert(form),
    announce_in_whats_new: Boolean(form.announce_in_whats_new),
    status: asAdmin ? 'published' : 'pending',
    submitted_by: authorId,
  }));
  const { data, error } = await supabase.from('bibliography').insert(rows).select();
  if (error) throw error;
  return data;
}

// Given a list of DOIs, return the subset (lowercased) that already exist in the
// bibliography the caller can see. Used to flag/skip duplicates on import.
// Best-effort: DOIs are case-insensitive but matched here by exact value, so a
// stored DOI in a different case could slip through — dedup is a convenience,
// not a hard guarantee.
export async function findExistingDois(dois) {
  const clean = [...new Set((dois ?? []).map((d) => d?.trim()).filter(Boolean))];
  if (clean.length === 0) return new Set();
  const { data, error } = await supabase.from('bibliography').select('doi').in('doi', clean);
  if (error) throw error;
  return new Set((data ?? []).map((r) => (r.doi ?? '').toLowerCase()));
}

// Admin-only direct update. RLS will reject if not admin.
export async function updateEntry(id, form) {
  const row = normalizeForInsert(form);
  const { data, error } = await supabase
    .from('bibliography')
    .update(row)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// Admin-only delete. RLS will reject if not admin.
// Announce an already-published entry in What's New. Admin only (RLS): the
// trigger from migration 019 turns the flag into a 30-day notice. Returns the
// updated row so the caller can show the "already announced" state.
export async function announceEntry(id) {
  const { data, error } = await supabase
    .from('bibliography')
    .update({ announce_in_whats_new: true })
    .eq('id', id)
    .select(SELECT_COLUMNS)
    .single();
  if (error) throw error;
  return data;
}

// Prefill for /whats-new/new when a non-admin wants to announce an entry:
// they post a pending What's New item instead of flagging the row.
export function whatsNewPrefillFor(entry) {
  const byline = (entry.authors ?? []).join('; ') + (entry.year ? ` (${entry.year})` : '');
  const doiUrl = entry.doi ? (/^https?:\/\//i.test(entry.doi) ? entry.doi : `https://doi.org/${entry.doi}`) : '';
  return {
    title: entry.title ?? '',
    type: 'new_publication',
    description: byline,
    url: entry.url || doiUrl || '',
  };
}

export async function deleteEntry(id) {
  const { error } = await supabase.from('bibliography').delete().eq('id', id);
  if (error) throw error;
}

// Participant-only edit suggestion. Stored separately; original entry untouched
// until an admin approves and applies the changes.
export async function suggestEdit(originalEntry, form, { authorId, reason }) {
  const changes = diffEntries(originalEntry, form);
  if (Object.keys(changes).length === 0) {
    throw new Error('No changes to submit');
  }
  const { data, error } = await supabase
    .from('bibliography_edits')
    .insert({
      bibliography_id: originalEntry.id,
      suggested_changes: changes,
      reason: reason?.trim() || null,
      submitted_by: authorId,
      status: 'pending',
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ────────── Read a single entry ──────────

export async function fetchEntry(id) {
  const { data, error } = await supabase
    .from('bibliography')
    .select(SELECT_COLUMNS)
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data;
}
